import { useDispatch } from 'react-redux';

// material-ui
import { Button, DialogActions, DialogContent, DialogTitle, Divider, Grid, InputLabel, Stack, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import _ from 'lodash';
import * as Yup from 'yup';
import { Form, FormikProvider, FormikValues, useFormik } from 'formik';

// project imports
import { openSnackbar } from 'store/reducers/snackbar';

// assets
import {
  MerchantCashAdvance,
  Transaction,
  useSaveTransactionNoteMutation
} from '../../graphql/types';

export interface Props {
  entity: Transaction;
  mca: MerchantCashAdvance;
  onCancel: () => void;
  onSave: (transaction: Transaction) => void;
}

const EditTransactionNote = ({ mca, entity, onCancel, onSave }: Props) => {
  const dispatch = useDispatch();
  const [updateTransactionNote] = useSaveTransactionNoteMutation();
  const CustomerSchema = Yup.object().shape({});

  const formik = useFormik({
    initialValues: {
      notes: entity.notes || ''
    },
    validationSchema: CustomerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const saved = await updateTransactionNote({
          variables: {
            id: entity.id,
            note: values.notes
          }
        });
        onSave(saved.data?.saveTransactionNote as Transaction);
        if (entity) {
          dispatch(
            openSnackbar({
              open: true,
              message: 'Transaction note updated successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        }

        setSubmitting(false);
        onCancel();
      } catch (error) {
        console.error(error);
      }
    }
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps, handleBlur, setFieldValue, handleChange } = formik;

  return (
    <FormikProvider value={formik}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle>Edit Transaction Note</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={1.25}>
                  <InputLabel htmlFor="transaction-entity-description">Notes</InputLabel>
                  <TextField
                    variant="filled"
                    fullWidth
                    id="transaction-entity-description"
                    placeholder="Enter Notes"
                    {...getFieldProps('notes')}
                    error={Boolean(touched.notes && errors.notes)}
                    helperText={touched.notes && errors.notes}
                  />
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>&nbsp;</Grid>
              <Grid item>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button color="error" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    Save Note
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </DialogActions>
        </Form>
      </LocalizationProvider>
    </FormikProvider>
  );
};

export default EditTransactionNote;
